import { useApiTradeV1ConvertPriceToMarketIndexPercentageRetrieve } from 'api';
import { formatDate } from 'date';
import { Box, formatNumber, Link, Stack, Typography, TypographyIcon } from 'ui';
import { LocalShippingOutlined, Person2Outlined, SupportAgentOutlined, WarehouseOutlined } from 'ui/icons';

import { useMatchedSiteMaterialItem } from '../../contexts/MatchedSiteMaterialItemContext';
import { formatMatchPrice } from '../../utils/formatMatchPrice/formatMatchPrice';
import { MatchedPriceOverview } from '../MatchedPriceOverview/MatchedPriceOverview';
import { MatchedSiteMaterialDetailsSectionTitle } from '../MatchedSiteMaterialDemandSignalItem/styles';

export const MatchedSupplySignalOverview = ({
  offerPrice,
  offerPriceCurrency,
  variant = 'default',
  showPrice = true,
}: {
  offerPrice?: string;
  offerPriceCurrency?: string;
  variant?: 'default' | 'muted';
  showPrice?: boolean;
}) => {
  const { supplySignal, siteMaterial } = useMatchedSiteMaterialItem();
  const {
    seller,
    seller_lead,
    tonnes,
    last_collection_date,
    hubspot_url,
    price_per_tonne,
    currency,
    price_as_index_percentage,
    price_index,
  } = supplySignal || {};
  const sellerName = seller?.name || (seller_lead?.name && `${seller_lead.name} (lead)`);
  const formattedSignalPrice = formatMatchPrice({
    currency,
    priceAsIndexPercentage: price_as_index_percentage ?? undefined,
    priceIndex: price_index ?? undefined,
    pricePerTonne: price_per_tonne ?? undefined,
  });
  const textVariant = variant === 'default' ? 'body1' : 'body2';

  const { data: indexPercentage } = useApiTradeV1ConvertPriceToMarketIndexPercentageRetrieve(
    {
      currency: offerPriceCurrency ?? undefined,
      material_type_id: siteMaterial.material_spec.material.material_type.id,
      price: offerPrice ? Number(offerPrice) : 0,
    },
    {
      query: {
        enabled: Boolean(offerPrice) && Boolean(offerPriceCurrency) && Boolean(supplySignal?.price_as_index_percentage),
      },
    }
  );

  const formatOfferPrice = formatMatchPrice({
    currency: offerPriceCurrency,
    priceAsIndexPercentage: indexPercentage?.price_as_index_percentage,
    priceIndex: supplySignal?.price_index,
    pricePerTonne: offerPrice,
  });

  return (
    <>
      <Stack direction="row" gap={1}>
        <MatchedSiteMaterialDetailsSectionTitle noWrap type={variant}>
          Supply signal
        </MatchedSiteMaterialDetailsSectionTitle>
        <Link href={hubspot_url} target="_blank" rel="noreferrer noopener">
          View Hubspot
        </Link>
      </Stack>
      {supplySignal ? (
        <>
          <Stack direction="column" gap={1.5} mb={2.5}>
            <TypographyIcon variant={textVariant} icon={Person2Outlined}>
              {sellerName}
            </TypographyIcon>
            <TypographyIcon variant={textVariant} icon={WarehouseOutlined}>
              Volume available:{' '}
              {tonnes
                ? formatNumber(tonnes, {
                    maximumFractionDigits: 0,
                    style: 'ton',
                  })
                : '-'}
            </TypographyIcon>
            <TypographyIcon variant={textVariant} icon={SupportAgentOutlined}>
              {seller?.account_manager?.short_name || '-'}
            </TypographyIcon>
            {last_collection_date ? (
              <TypographyIcon
                variant={textVariant}
                title="Last collection date"
                icon={LocalShippingOutlined}
                data-testid="collection-date"
              >
                {formatDate(last_collection_date)}
              </TypographyIcon>
            ) : null}
          </Stack>

          {showPrice ? (
            <>
              <Box mb={2}>
                <Typography variant="body2" fontWeight="bold">
                  Pricing
                </Typography>
              </Box>

              <MatchedPriceOverview
                prices={[
                  {
                    label: 'Live supply signal price',
                    price: formattedSignalPrice,
                  },
                  {
                    label: 'Safi price offer',
                    price: formatOfferPrice,
                  },
                ]}
              />
            </>
          ) : null}
        </>
      ) : (
        <Typography>No supply signal found.</Typography>
      )}
    </>
  );
};
